import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/categories';
import type { ArticleItem } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: ArticleItem | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <div className="w-20 h-20 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="text-foreground-950 text-xl font-semibold mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">您要查看的文章不存在或已被移除</p>
          <Link to="/category" className="inline-block px-6 py-2.5 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap">
            返回分类浏览
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const catInfo = categories[article.category];
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category' },
    { label: getCategoryName(article.category), href: `/category?tab=${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '17.c-起草口',
    },
    url: `https://zhimycloud.cn/article/${article.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `https://zhimycloud.cn${item.href}` : undefined,
    })),
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 17.c-起草口 | 专业文案起草指南`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonical={`/article/${article.slug}`}
        ogType="article"
      />

      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm">
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                <span>{article.author}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span>{article.publishDate}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                <span>阅读约 {article.readTime} 分钟</span>
              </div>
              {catInfo && (
                <Link
                  to={`/category?tab=${article.category}`}
                  className="flex items-center gap-1.5 text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
                >
                  <i className={catInfo.icon}></i>
                  <span>{catInfo.name}</span>
                </Link>
              )}
            </div>
          </header>

          {/* Content */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-h2:text-foreground-950 prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-ul:list-disc prose-ul:pl-5"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Author Card */}
          <div className="mt-10 p-5 bg-background-100 rounded-lg flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center shrink-0">
              <span className="text-accent-600 font-semibold">{article.author[0]}</span>
            </div>
            <div>
              <div className="text-foreground-950 font-medium text-sm">{article.author}</div>
              <div className="text-foreground-500 text-xs">17.c-起草口 特约作者</div>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-10 border-t border-background-200">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="bg-background-100 rounded-lg p-4 hover:bg-background-200/70 transition-colors duration-200"
                  >
                    <h3 className="text-foreground-950 font-medium text-sm mb-2 line-clamp-2 hover:text-accent-600 transition-colors">
                      {ra.title}
                    </h3>
                    <p className="text-foreground-500 text-xs line-clamp-2 mb-3">{ra.excerpt}</p>
                    <div className="flex items-center gap-2 text-foreground-400 text-xs">
                      <span>{ra.author}</span>
                      <span>·</span>
                      <span>{ra.readTime} 分钟阅读</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Back Link */}
          <div className="mt-8">
            <Link
              to={`/category?tab=${article.category}`}
              className="inline-flex items-center gap-2 text-accent-600 hover:text-accent-700 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{getCategoryName(article.category)}分类
            </Link>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}